/**
 * gwp-auto-add.js — gift-with-purchase cart automation.
 *
 * Adds the configured gift product to the cart when the eligible subtotal
 * (items_subtotal_price minus the gift line itself) reaches the minimum
 * spend, and removes it again when the cart drops back below. Config comes
 * from Theme Settings › Gift with purchase via window.theme.gwpData
 * (snippets/gwp-data.liquid); the threshold is authored in shop currency and
 * converted with the shared tier engine's toPresentment().
 *
 * The theme never fakes the price — a matching free-gift discount in admin
 * (Multiscount) makes the line $0 at checkout, so a gift smuggled in below
 * the threshold is simply a paid product.
 *
 * A shopper who deletes the gift while still eligible has opted out: that is
 * remembered for the session (sessionStorage) and the gift is not re-added.
 */
(function () {
  'use strict';

  var DISMISSED_KEY = 'theme:gwp:dismissed';
  var ADDED_KEY = 'theme:gwp:added';
  var busy = false;

  function config() {
    var data = window.theme && window.theme.gwpData;
    if (!data || data.enabled !== true || !data.variant_id || !data.min) return null;
    return data;
  }

  function minCents(data) {
    var BT = window.theme && window.theme.bundleTiers;
    return BT ? BT.toPresentment(data.min) : data.min;
  }

  function readFlag(key) {
    try {
      return sessionStorage.getItem(key) === '1';
    } catch (e) {
      return false;
    }
  }

  function writeFlag(key, on) {
    try {
      if (on) {
        sessionStorage.setItem(key, '1');
      } else {
        sessionStorage.removeItem(key);
      }
    } catch (e) {
      /* private mode — degrade to add-only behaviour */
    }
  }

  function refreshCartUI() {
    if (document.getElementById('cart-drawer')) {
      document.dispatchEvent(new CustomEvent('theme:cart:refresh', { bubbles: true }));
    } else if (window.location.pathname.indexOf('/cart') === 0) {
      window.location.reload();
    }
  }

  function addGift(data) {
    busy = true;
    fetch(window.theme.routes && window.theme.routes.cart_add_url ? window.theme.routes.cart_add_url + '.js' : '/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: data.variant_id, quantity: 1 }),
    })
      .then(function (res) {
        if (res.ok) {
          writeFlag(ADDED_KEY, true);
          refreshCartUI();
        }
      })
      .catch(function () {
        /* sold out or race — try again on the next cart change */
      })
      .finally(function () {
        busy = false;
      });
  }

  function removeGift(giftLine) {
    busy = true;
    fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: giftLine.key, quantity: 0 }),
    })
      .then(function (res) {
        if (res.ok) {
          writeFlag(ADDED_KEY, false);
          refreshCartUI();
        }
      })
      .catch(function () {})
      .finally(function () {
        busy = false;
      });
  }

  function evaluate() {
    var data = config();
    if (!data || busy) return;

    fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (res) {
        return res.ok ? res.json() : null;
      })
      .then(function (cart) {
        if (!cart || busy) return;

        var giftLine = null;
        cart.items.forEach(function (item) {
          if (!giftLine && item.product_id === data.product_id) giftLine = item;
        });

        // The gift never counts towards its own threshold.
        var eligibleSubtotal = cart.items_subtotal_price;
        if (giftLine) eligibleSubtotal -= giftLine.final_line_price;

        var eligible = eligibleSubtotal >= minCents(data);

        if (eligible && !giftLine) {
          if (readFlag(ADDED_KEY)) {
            // We added it this session and it's gone while still eligible:
            // the shopper removed it on purpose. Respect that.
            writeFlag(DISMISSED_KEY, true);
            writeFlag(ADDED_KEY, false);
            return;
          }
          if (!readFlag(DISMISSED_KEY)) addGift(data);
        } else if (!eligible && giftLine && readFlag(ADDED_KEY)) {
          // Only take back a gift the theme itself added — a shopper who put
          // the product in the cart to buy it keeps it.
          removeGift(giftLine);
          writeFlag(DISMISSED_KEY, false);
        } else if (!eligible) {
          // Below the threshold the opt-out resets — crossing it again is a
          // fresh offer.
          writeFlag(DISMISSED_KEY, false);
          writeFlag(ADDED_KEY, false);
        }
      })
      .catch(function () {});
  }

  var debounceTimer = null;
  function scheduleEvaluate() {
    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(evaluate, 150);
  }

  document.addEventListener('theme:cart:added', scheduleEvaluate);
  document.addEventListener('theme:cart:update', scheduleEvaluate);
  document.addEventListener('theme:cart:loaded', scheduleEvaluate);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', scheduleEvaluate);
  } else {
    scheduleEvaluate();
  }
})();
